import { useEffect, useState } from 'react';
import { Button, Input, Radio, Tooltip } from '@fluentui/react-components';
import { DismissRegular } from '@fluentui/react-icons';

import { MAX_DROPDOWN_OPTION_LENGTH } from './dropdownFieldUtils';

type TProps = {
  option: string;
  canRemove: boolean;
  refInput: (element: HTMLInputElement | null) => void;
  // Each returns the option's text after the commit: the trimmed text, or the earlier text when it was refused.
  onCommit: (text: string) => string;
  onEnter: (text: string) => string;
  onRemove: () => void;
};

/** One option of a Dropdown: its radio in the panel's group picks the default, its text is kept until Enter or leaving it. */
const DropdownOptionRow = ({ option, canRemove, refInput, onCommit, onEnter, onRemove }: TProps) => {
  const [draft, setDraft] = useState(option);

  // The committed option, also after an undo or redo, so a later blur cannot bring an undone text back.
  useEffect(() => setDraft(option), [option]);

  return (
    <div className="flex w-full items-center gap-1">
      <Radio value={option} />
      <Input
        className="grow"
        size="small"
        value={draft}
        maxLength={MAX_DROPDOWN_OPTION_LENGTH}
        input={{ ref: refInput }}
        onChange={(_, data) => setDraft(data.value)}
        onBlur={() => setDraft(onCommit(draft))}
        onKeyDown={(event) => {
          // Typed text keeps the input's own undo until it is committed.
          if (draft !== option) event.stopPropagation();
          if (event.key !== 'Enter') return;
          event.preventDefault();
          setDraft(onEnter(draft));
        }}
      />
      <Tooltip content="Remove option" relationship="label" withArrow>
        <Button appearance="subtle" size="small" icon={<DismissRegular />} disabled={!canRemove} onClick={onRemove} />
      </Tooltip>
    </div>
  );
};

export default DropdownOptionRow;
